import React, { FormEvent, useRef, useState, useEffect } from "react";
import KrgTextInput, {
  THEME_ENUM,
  VARIANT_ENUM,
} from "@kargo/shared-components.krg-text-input";
import {
  Box,
  FormControl,
  FormGroup,
  Grid,
  InputLabel,
  Typography,
} from "@mui/material";
import KrgChip, { TYPE_ENUM } from "@kargo/shared-components.krg-chip";
import { ChipData } from "@kargo/shared-components.krg-chip/dist/shared/interfaces";
import KrgSelect, {
  ItemI,
  MULTIPLE_TYPE_ENUM,
  SIZE_ENUM,
} from "@kargo/shared-components.krg-select";
import { VideoManagement } from "./components";
import {
  GenerateFormType,
  PlaylistCategoryInput,
  PlaylistTagInput,
} from "../../../@types/playlist.types";
import { Video } from "../../../api/types";
import { useFormContext } from "../../../context/form/form.provider";
import { FormActionsEnum } from "../../../@types/form.types";
import {
  handleAddTag,
  handleDeleteTag,
  handleOnChange,
  submitForm,
} from "../../../context/form/form.actions";
import { HinterText } from "../../../pages/media-gallery/single-media/SingleMediaEditor";
import LoadingCircle from "../../LoadingCircle";
import ContentModal from "../ContentModal";
import InfoPopover from "../../InfoPopover";
import usePlaylists from "../../../hooks/usePlaylists";
import useSnackbar from "../../../hooks/useSnackbar";
import { capitalizeStrings } from "../../../helpers/capitilizeStrings";
import "./styles.scss";
import { tier1Categories } from "../../../constants/iab-t1-categories-v3";
import { usePublisherContext } from "../../../hooks/usePublisherProvider";

type GeneratePlaylistByTagProps = {
  type: GenerateFormType;
  isOpen?: boolean;
  onClose: () => void;
  asAdmin?: boolean;
};

export const GeneratePlaylistByType = ({
  type,
  isOpen,
  onClose,
  asAdmin,
}: GeneratePlaylistByTagProps) => {
  const [{ formInputFields, errors }, dispatch] = useFormContext();
  const { publisherSlug } = usePublisherContext(asAdmin);
  const excludedTagInputRef = useRef<HTMLInputElement>();
  const tagInputRef = useRef<HTMLInputElement>();
  const { title, tags, categories, excludedTags, sort } = formInputFields;
  const { api } = usePlaylists();
  const { snackbarError, snackbarSuccess } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedVideos, setSelectedVideos] = useState<Video[]>([]);
  const [sortedVideos, setSortedVideos] = useState<Video[]>([]);
  const [isAddVideosModalOpen, setIsAddVideosModalOpen] = useState(false);

  // Set default sort type when modal opens
  useEffect(() => {
    if (type === "custom" && !sort.value) {
      handleOnChange(dispatch, sort, "newest");
    }
  }, [type, sort.value, dispatch, sort]);

  const parseFormToRequestData = () => {
    const data: any = {
      title: title.value,
      key: (title.value as string).trim().replace(/ /g, "-"),
      sortType: sort.value,
      categories: categories.value,
      tags: (tags.value as ChipData[]).map((tag) => tag.text),
      excludedTags: (excludedTags.value as ChipData[]).map((tag) => tag.text),
      ...(publisherSlug && { slug: publisherSlug }),
    };
    if (type === "category") {
      delete data.excludedTags;
      delete data.tags;
    } else if (type === "custom") {
      delete data.excludedTags;
      delete data.tags;
      delete data.categories;
      // Send videos in the sorted order
      data.medias = sortedVideos.map((video) => video.token);
    } else {
      delete data.categories;
    }
    return data;
  };

  const cleanAndCloseForm = () => {
    dispatch({
      type: FormActionsEnum.RESET_FORM,
      payload: {
        ...formInputFields,
        sort: { ...sort, value: "" },
        categories: { ...categories, value: [] },
        tags: { ...tags, value: [] },
        excludedTags: { ...excludedTags, value: [] },
        title: { ...title, value: "" },
      },
    });
    onClose();
  };

  const handleSelect = (value) => {
    setSelectedCategories(value);
    handleOnChange(dispatch, categories, value);
  };

  const handleRequestByType = (
    data: PlaylistCategoryInput | PlaylistTagInput,
  ) => {
    setIsLoading(true);
    const onSuccess = () => {
      dispatch({ type: FormActionsEnum.SET_FORM_SUBMITTED });
      snackbarSuccess(`Playlist ${data.title} created`);
      cleanAndCloseForm();
    };

    const onError = (error: any) => {
      const errorMessage =
        error?.message || "Something went wrong, try again later";
      snackbarError(errorMessage);
    };

    if (type === "category") {
      api.createByCategory(data, {
        onSuccess,
        onError,
        onSettled: () => setIsLoading(false),
      });
    } else if (type === "custom") {
      api.create(data, {
        onSuccess,
        onError,
        onSettled: () => setIsLoading(false),
      });
    } else {
      api.createByTag(data, {
        onSuccess,
        onError,
        onSettled: () => setIsLoading(false),
      });
    }
  };

  const submitHandler = async (ev: FormEvent | MouseEvent) => {
    ev.preventDefault();
    const data = parseFormToRequestData();
    submitForm(dispatch, errors, data, handleRequestByType);
  };

  const allRequiredIsFilled = () => {
    const formFieldsValid = !Object.keys(formInputFields).some(
      (k) => formInputFields[k].required && !formInputFields[k].value,
    );

    return formFieldsValid;
  };

  return (
    <>
      <ContentModal
        title={`${type === "custom" ? "Create a new custom playlist" : `Generate Playlist by ${capitalizeStrings(type)}`}`}
        content={
          isLoading ? (
            <Grid container justifyContent="center">
              <LoadingCircle style={{ height: 50, width: 50, margin: 50 }} />
            </Grid>
          ) : (
            <div className="content-box">
              <Box id="media-editor-form-container">
                <Box
                  display="flex"
                  width="100%"
                  justifyContent="space-around"
                  gap={2}
                >
                  {/* title */}
                  {title && (
                    <FormGroup
                      className={`form-group-container ${errors?.title && "error-field-group"}`}
                      sx={{ width: "100%" }}
                    >
                      <InputLabel>{title.label}</InputLabel>
                      <FormControl error={!!errors?.title}>
                        <KrgTextInput
                          id="media-title"
                          name={title.name || "title"}
                          variant={VARIANT_ENUM.outlined}
                          text={title.value?.toString() || ""}
                          style={{ height: "auto", width: "100%" }}
                          onTextChange={(text) =>
                            handleOnChange(dispatch, title, text)
                          }
                        />
                        <HinterText
                          text={title.hinterText}
                          errors={errors?.title}
                        />
                      </FormControl>
                    </FormGroup>
                  )}
                  {/* sort */}
                  {sort && sort.options && (
                    <FormGroup
                      className={`form-group-container ${errors?.sort && "error-field-group"}`}
                      sx={{ width: "100%" }}
                    >
                      <InputLabel style={{ marginLeft: 16 }}>
                        {sort.label}
                        {sort.popoverText && (
                          <InfoPopover text={sort.popoverText} />
                        )}
                      </InputLabel>
                      <FormControl error={!!errors?.sort}>
                        <KrgSelect
                          style={{ width: "100%" }}
                          value={sort.value as string}
                          items={
                            type === "custom"
                              ? (sort.options as ItemI[])
                              : (sort.options as ItemI[]).filter(
                                  (option) => option.value !== "manual",
                                )
                          }
                          onChange={(value: string) =>
                            handleOnChange(dispatch, sort, value)
                          }
                        />
                        <HinterText
                          text={sort.hinterText}
                          errors={errors?.sort}
                        />
                      </FormControl>
                      {sort.value === "shuffle" && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            marginTop: 1,
                            fontStyle: "italic",
                            fontSize: "12px",
                          }}
                        >
                          Preview won&apos;t reflect the shuffled playback
                          sequence.
                        </Typography>
                      )}
                    </FormGroup>
                  )}
                </Box>
                {type === "category" && (
                  <>
                    {/* category */}
                    {categories && (
                      <FormGroup
                        className={`form-group-container ${errors?.categories && "error-field-group"}`}
                      >
                        <InputLabel>
                          {categories.label}
                          {categories.popoverText && (
                            <InfoPopover text={categories.popoverText} />
                          )}
                        </InputLabel>
                        <FormControl error={!!errors?.categories}>
                          <KrgSelect
                            value={selectedCategories}
                            items={
                              tier1Categories.map((cat) => ({
                                text: cat.name,
                                value: cat.name?.toLowerCase(),
                              })) as ItemI[]
                            }
                            multipleType={MULTIPLE_TYPE_ENUM.checkmark}
                            size={SIZE_ENUM.small}
                            onChange={(value: string) => handleSelect(value)}
                            isMultiple
                          />
                        </FormControl>
                      </FormGroup>
                    )}
                  </>
                )}

                {type === "custom" && (
                  <>
                    {/* custom playlist - video selection area */}
                    <VideoManagement
                      selectedVideos={selectedVideos}
                      setSelectedVideos={setSelectedVideos}
                      sortValue={sort.value as string}
                      publisherSlug={publisherSlug}
                      asAdmin={asAdmin}
                      isAddVideosModalOpen={isAddVideosModalOpen}
                      setIsAddVideosModalOpen={setIsAddVideosModalOpen}
                      onSortedVideosChange={setSortedVideos}
                    />
                  </>
                )}

                {type === "tag" && (
                  <>
                    {/* tags */}
                    {tags && (
                      <FormGroup
                        className={`form-group-container ${errors?.tags && "error-field-group"}`}
                      >
                        <InputLabel>
                          {tags.label}
                          {tags.popoverText && (
                            <InfoPopover text={tags.popoverText} />
                          )}
                        </InputLabel>
                        <FormControl error={!!errors?.tags}>
                          <Box
                            className="chip-container-tag"
                            onClick={() => {
                              tagInputRef.current?.focus();
                            }}
                          >
                            {tags.value &&
                              (tags.value as ChipData[]).map((t: ChipData) => (
                                <KrgChip
                                  key={`chip-${t.id}`}
                                  theme={THEME_ENUM.v2}
                                  type={TYPE_ENUM.gray}
                                  data={{ text: t.text, id: t.id }}
                                  onDelete={() =>
                                    handleDeleteTag(dispatch, t, tags)
                                  }
                                />
                              ))}
                            <input
                              id="tag"
                              type="text"
                              onKeyDown={(ev) =>
                                handleAddTag(dispatch, ev, tags, tagInputRef)
                              }
                              placeholder="Add a tag"
                              ref={tagInputRef}
                            />
                          </Box>
                        </FormControl>
                      </FormGroup>
                    )}
                    {/* excluded tags */}
                    {excludedTags && (
                      <FormGroup
                        className={`form-group-container ${errors?.excludedTags && "error-field-group"}`}
                      >
                        <InputLabel>
                          {excludedTags.label}
                          {excludedTags.popoverText && (
                            <InfoPopover text={excludedTags.popoverText} />
                          )}
                        </InputLabel>
                        <FormControl error={!!errors?.excludedTags}>
                          <Box
                            className="chip-container-tag"
                            onClick={() => {
                              excludedTagInputRef.current?.focus();
                            }}
                          >
                            {excludedTags.value &&
                              (excludedTags.value as ChipData[]).map(
                                (t: ChipData) => (
                                  <KrgChip
                                    key={`chip-${t.id}`}
                                    className="excluded-chip"
                                    theme={THEME_ENUM.v2}
                                    type={TYPE_ENUM.gray}
                                    data={{ text: t.text, id: t.id }}
                                    onDelete={() =>
                                      handleDeleteTag(dispatch, t, excludedTags)
                                    }
                                  />
                                ),
                              )}
                            <input
                              id="exclued-tag"
                              type="text"
                              onKeyDown={(ev) =>
                                handleAddTag(
                                  dispatch,
                                  ev,
                                  excludedTags,
                                  excludedTagInputRef,
                                )
                              }
                              placeholder="Add a tag"
                              ref={excludedTagInputRef}
                            />
                          </Box>
                        </FormControl>
                      </FormGroup>
                    )}
                  </>
                )}
              </Box>
            </div>
          )
        }
        isOpen={isOpen}
        onClose={onClose}
        submitButtonText="Generate"
        cancelButtonText="cancel"
        onSubmit={submitHandler}
        isSubmitButtonEnabled={allRequiredIsFilled()}
      />
    </>
  );
};
